/**
 * ProcedeCard — composant unifie pour afficher un procede dans tous les
 * contextes du module Seances :
 *
 * - context="insightlab" : carte cliquable, navigate vers detail, menu
 *                          actions (Voir / Dupliquer / Modifier / Supprimer)
 * - context="seance"     : carte d'un bloc dans une seance (lecture).
 *                          Affiche numero de position, pas de menu actions
 *                          (V1 consultation), pas de navigation au clic.
 * - context="modele"     : meme rendu que "seance" mais pour un bloc d'un
 *                          template (futur).
 * - context="suggeree"   : usage futur, idem.
 *
 * La prop `data` peut etre un Exercise (catalogue) ou un bloc resolu
 * (cascade override > exercise > NULL faite par le backend).
 * Les champs sont resolus en interne via _meta(...) helpers.
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Eye, Copy, Pencil, Trash2 } from 'lucide-react'
import { T } from '../theme'
import PitchEditor from './PitchEditor'

const PROCEDE_LABELS = {
  echauffement: 'Échauffement',
  jeu: 'Jeu',
  situation: 'Situation',
  exercice: 'Exercice',
  match: 'Match',
}


// ─── Helpers de normalisation Exercise/Block ─────────────────────

function _name(d) { return d?.name || d?.source_exercise_name || '' }
function _procede(d) { return d?.procede || '' }
function _categorie(d) { return d?.category_tactique || '' }

function _duration(d) {
  // Exercise -> default_duration_minutes ; Block resolu -> duration_minutes
  return d?.duration_minutes ?? d?.default_duration_minutes ?? null
}
function _effectif(d) {
  return d?.effectif ?? d?.default_effectif ?? ''
}
function _surface(d) {
  return d?.surface ?? d?.default_surface ?? ''
}
function _description(d) {
  // Block resolu : pas de description native, on retombe sur consignes courte
  return d?.description || ''
}
function _schema(d) {
  // Le backend serialise `schema` (alias Pydantic) en JSON => OK des deux cotes
  return d?.schema || d?.pitch_schema || { tokens: [], arrows: [], cones: [] }
}
function _orientation(d) {
  return d?.pitch_orientation || 'landscape'
}


function Badge({ label, color, bg, border }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      background: bg, color, border: `1px solid ${border}`,
      fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em',
      textTransform: 'uppercase', fontWeight: 700,
    }}>
      {label}
    </span>
  )
}


function ActionMenu({ items, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) onClose() }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [onClose])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute', right: 0, top: '110%',
        background: T.surface, border: `2px solid ${T.dark}`,
        boxShadow: `4px 4px 0 ${T.dark}`,
        minWidth: 160, zIndex: 50,
      }}
    >
      {items.map((it, i) => it.divider ? (
        <div key={i} style={{ height: 1, background: T.rule }} />
      ) : (
        <button
          key={i}
          type="button"
          disabled={it.disabled}
          onClick={(e) => { e.stopPropagation(); it.onClick(); onClose() }}
          style={{
            width: '100%', padding: '8px 12px', textAlign: 'left',
            background: 'transparent', border: 'none', cursor: it.disabled ? 'not-allowed' : 'pointer',
            opacity: it.disabled ? 0.4 : 1,
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: T.mono, fontSize: 11, color: it.danger ? T.red : T.ink,
          }}
          onMouseOver={(e) => { if (!it.disabled) e.currentTarget.style.background = T.bgAlt }}
          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  )
}


export default function ProcedeCard({
  // Donnee : Exercise (insightlab) ou bloc resolu (seance/modele/suggeree)
  // On garde l'alias `exercise` pour compat avec InsightlabTab qui l'utilise.
  exercise = null,
  data = null,
  context = 'insightlab',
  currentUserId = null,
  position = null,        // numero du bloc en context='seance'
  onDuplicate = null,
  onDelete = null,
}) {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const d = data || exercise || {}

  const isInsightlab = context === 'insightlab'
  const isSeance = context === 'seance' || context === 'modele' || context === 'suggeree'

  // Permissions (uniquement utiles en insightlab)
  const isOwner = isInsightlab && d.user_id === currentUserId
  const isSystemTemplate = isInsightlab && d.is_template && d.user_id === null
  const canEdit = isOwner && !isSystemTemplate
  const canDelete = canEdit
  const isClubShared = isInsightlab && d.club_id && !isOwner

  const procedeLabel = PROCEDE_LABELS[_procede(d)] || _procede(d)

  // Metadonnees compactes
  const dur = _duration(d)
  const eff = _effectif(d)
  const surf = _surface(d)
  const meta = [dur && `${dur} min`, eff, surf].filter(Boolean).join(' · ')

  const desc = _description(d)
  const showMenu = isInsightlab

  // Cible de navigation au clic :
  // - context='insightlab' : detail du procede via son id
  // - context='seance'/'modele'/'suggeree' : detail du procede source via
  //   exercise_id (le bloc resolu reference l'Exercise du catalogue).
  //   Si exercise_id est null (bloc custom from-scratch), pas de navigation.
  let navigationTargetId = null
  if (isInsightlab) navigationTargetId = d.id || null
  else if (isSeance) navigationTargetId = d.exercise_id || null

  const clickable = !!navigationTargetId

  const handleCardClick = () => {
    if (!navigationTargetId) return
    navigate(`/dashboard/seances/insightlab/${navigationTargetId}`)
  }

  return (
    <div
      onClick={handleCardClick}
      style={{
        background: T.surface,
        border: `2px solid ${T.dark}`,
        boxShadow: `4px 4px 0 ${T.dark}`,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform .12s, box-shadow .12s',
        display: 'flex', flexDirection: 'column',
        position: 'relative',
      }}
      onMouseOver={clickable ? (e) => {
        e.currentTarget.style.transform = 'translate(-1px, -1px)'
        e.currentTarget.style.boxShadow = `5px 5px 0 ${T.dark}`
      } : undefined}
      onMouseOut={clickable ? (e) => {
        e.currentTarget.style.transform = 'translate(0, 0)'
        e.currentTarget.style.boxShadow = `4px 4px 0 ${T.dark}`
      } : undefined}
    >
      {/* Position badge en context='seance' */}
      {isSeance && position != null && (
        <div style={{
          position: 'absolute', top: -10, left: -10,
          width: 28, height: 28,
          background: T.gold, color: T.dark,
          border: `2px solid ${T.dark}`,
          fontFamily: T.display, fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 5,
        }}>
          {position}
        </div>
      )}

      {/* Mini preview du schema */}
      <div style={{
        height: 180,
        borderBottom: `2px solid ${T.dark}`,
        background: T.dark,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}>
        <div style={{ transform: 'scale(1)', transformOrigin: 'center' }}>
          <PitchEditor
            initialSchema={_schema(d)}
            initialOrientation={_orientation(d)}
            readOnly
            showToolbar={false}
            height="180px"
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Procede + categorie */}
        <div style={{
          fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em',
          textTransform: 'uppercase', color: T.gold, fontWeight: 700,
        }}>
          {procedeLabel}{_categorie(d) ? ` · ${_categorie(d)}` : ''}
        </div>

        {/* Nom */}
        <h3 style={{
          fontFamily: T.display, fontSize: 18, color: T.ink,
          textTransform: 'uppercase', letterSpacing: '.01em',
          lineHeight: 1.1, margin: 0,
        }}>
          {_name(d)}
        </h3>

        {/* Metadata */}
        {meta && (
          <div style={{
            fontFamily: T.mono, fontSize: 10, color: T.muted2,
            letterSpacing: '.04em',
          }}>
            {meta}
          </div>
        )}

        {/* Description */}
        {desc && (
          <p style={{
            fontFamily: T.mono, fontSize: 11, color: T.ink2,
            lineHeight: 1.5, margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {desc}
          </p>
        )}

        {/* Footer : badges + (menu en insightlab) */}
        <div style={{
          marginTop: 'auto', paddingTop: 10,
          borderTop: `1px solid ${T.rule}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {isSystemTemplate && (
              <Badge label="Système" color={T.gold} bg={T.dark} border={T.dark} />
            )}
            {isClubShared && (
              <Badge label="Club" color="#fff" bg={T.blue} border={T.blue} />
            )}
            {isInsightlab && !d.is_template && isOwner && (
              <Badge label="Perso" color={T.dark} bg={T.bgAlt} border={T.dark} />
            )}
            {isSeance && d.has_overrides && (
              <Badge label="Modifié" color={T.gold} bg={T.dark} border={T.dark} />
            )}
            {isSeance && d.is_custom && (
              <Badge label="Custom" color="#fff" bg={T.blue} border={T.blue} />
            )}
          </div>

          {showMenu && (
            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setMenuOpen(v => !v)}
                aria-label="Actions"
                style={{
                  background: 'transparent', border: `1.5px solid ${T.rule}`,
                  padding: 4, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <MoreVertical size={14} color={T.ink2} />
              </button>
              {menuOpen && (
                <ActionMenu
                  onClose={() => setMenuOpen(false)}
                  items={[
                    {
                      icon: <Eye size={12} />, label: 'Voir le détail',
                      onClick: () => navigate(`/dashboard/seances/insightlab/${d.id}`),
                    },
                    {
                      icon: <Copy size={12} />, label: 'Dupliquer',
                      onClick: () => onDuplicate?.(d),
                    },
                    ...(canEdit ? [{
                      icon: <Pencil size={12} />, label: 'Modifier',
                      onClick: () => navigate(`/dashboard/seances/insightlab/${d.id}/edit`),
                    }] : []),
                    ...(canDelete ? [
                      { divider: true },
                      {
                        icon: <Trash2 size={12} />, label: 'Supprimer', danger: true,
                        onClick: () => onDelete?.(d),
                      },
                    ] : []),
                  ]}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
